import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { Mark } from '@skin/Mark'
import { TerrainField } from '@skin/TerrainField'
import { meta } from '@skin/meta'
import { authApi } from '../../api/auth'
import { RefusedError } from '../../api/client'
import { useSession } from '../../hooks/useSession'
import { useSpoke } from '../../hooks/useSpoke'

// The landing screen: a command-and-control terrain field. Everything on the HUD is either brand (skin),
// tenant (useSpoke), or the clock — no fabricated metrics, no fake counts. The terrain is atmosphere.
export function Login() {
  const { signIn } = useSession()
  const spoke = useSpoke()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [clock, setClock] = useState(() => utcClock())
  const formRef = useRef<HTMLFormElement>(null)
  // The deployment decides what a person signs in with (a value-pack seed field); the browser only labels it.
  const deployment = useQuery({ queryKey: ['deployment'], queryFn: authApi.deployment, staleTime: Infinity })
  const byEmail = (deployment.data?.signInIdentifier ?? 'email') === 'email'
  const from = (location.state as { from?: string } | null)?.from ?? '/'

  useEffect(() => {
    const id = window.setInterval(() => setClock(utcClock()), 1000)
    return () => window.clearInterval(id)
  }, [])

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await signIn(email, password)
      navigate(from, { replace: true })
    } catch (err) {
      setError(err instanceof RefusedError ? err.refusal.message : 'Could not reach Scout.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="radar-stage">
      <TerrainField anchor={formRef} />
      <div className="radar-scanlines" />
      <div className="radar-vignette" />
      <div className="radar-hud" aria-hidden="true">
        <span className="hud-corner tl" />
        <span className="hud-corner tr" />
        <span className="hud-corner bl" />
        <span className="hud-corner br" />
        <div className="hud-readout top">
          <span><span className="dot" />Scan active</span>
          <span>Spoke <span className="live">{spoke}</span></span>
        </div>
        <div className="hud-readout right">
          <span>{clock} UTC</span>
        </div>
        <div className="hud-readout bottom">
          <span>{meta.productName} · sign in to your station</span>
        </div>
      </div>

      <div className="login-canvas">
        <form ref={formRef} className="login-card" onSubmit={submit}>
          <div className="brand brand-dark">
            <Mark size={36} />
            <span className="brand-name">{meta.productName}</span>
          </div>
          <p className="brand-sub">Business development intelligence</p>
          <p className="tagline">{meta.tagline}</p>
          <label>
            {byEmail ? 'Work email' : 'Sign-in name'}
            <input type={byEmail ? 'email' : 'text'} autoComplete="username" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </label>
          <label>
            Password
            <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </label>
          {error && (
            <div className="refusal" role="alert">
              <p>{error}</p>
            </div>
          )}
          <button type="submit" className="button primary" disabled={busy}>
            {busy ? meta.busyLabel : 'Engage'}
          </button>
          <div className="login-status">
            <span>Tenant</span>
            <strong>{spoke}</strong>
            <span>Link</span>
            <strong>{busy ? 'authenticating' : 'secure · local'}</strong>
          </div>
        </form>
      </div>
    </div>
  )
}

function utcClock(): string {
  return new Date().toISOString().slice(11, 19)
}
